import { BetaTestingStyle, IntegrationCapability, ReleaseStepType } from '@/types';
import {
    DefaultStepConfig,
    BetaTestingStepConfig,
    MetadataStepConfig,
    StoreReviewStepConfig,
    ReleaseStepConfig,
    SubmissionStepConfig,
    StepConfig,
    InitialEncryptionOptions,
} from '@/types/stepConfig';
import { Destination } from '@/types/destinations';
import { StepDependencies } from '@/types/workflow';

// Map integration installation ID prefixes to their capabilities
const getCapabilityForIntegrationId = (integrationId: string): IntegrationCapability | null => {
    const integrationPrefix = integrationId.split(':')[0];

    switch (integrationPrefix) {
        case 'github':
            return IntegrationCapability.VersionControl;
        case 'slack':
            return IntegrationCapability.Notifications;
        case 'jira':
            return IntegrationCapability.FeatureManagement;
        case 'bitrise':
            return IntegrationCapability.BuildSystem;
        case 'apple-beta':
        case 'google-beta':
            return IntegrationCapability.BetaTesting;
        case 'apple':
        case 'google':
            return IntegrationCapability.AppStore;
        case 'testrail':
            return IntegrationCapability.RegressionTesting;
        case 'crowdin':
            return IntegrationCapability.Translations;
        case 'sentry':
            return IntegrationCapability.StabilityMonitoring;
        case 'amplitude':
        case 'optimizely':
            return IntegrationCapability.ObservabilityAndAnalytics;
        case 'pagerduty':
            return IntegrationCapability.Scheduling;
        default:
            return null;
    }
};

const createBaseStepConfig = (
    id: string,
    name: string,
    destinations: Destination[] = [],
    integrationInstallationIds: string[] = [],
): Omit<DefaultStepConfig, 'settings' | 'archetype'> => {
    // Derive capabilities from integration IDs
    const capabilities = integrationInstallationIds
        .map((id) => getCapabilityForIntegrationId(id))
        .filter((cap): cap is IntegrationCapability => cap !== null);

    return {
        id,
        name,
        description: null,
        destinations,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        dependencies: {} as StepDependencies,
        requiredCapabilities: capabilities,
        optionalCapabilities: [],
        integrationInstallationIds,
        automations: {} as any,
        notifications: {} as any,
        tagConfigId: null,
        branchConfigId: null,
        featureAffiliationConfigId: null,
    };
};

export const mockStepConfigForArchetype = (
    stepConfigId: string,
    stepName: string,
    archetype: ReleaseStepType,
    destinations: Destination[] = [],
    integrationInstallationIds?: string[],
    upstreamCIStepConfigId?: string | null,
    upstreamBetaStepConfigId?: string | null,
): StepConfig => {
    const base = createBaseStepConfig(stepConfigId, stepName, destinations, integrationInstallationIds);

    const upstreamCIStepConfigOptions =
        upstreamCIStepConfigId ?
            {
                [upstreamCIStepConfigId]: {
                    id: upstreamCIStepConfigId,
                    name: 'CI',
                    description: null,
                    archetype: ReleaseStepType.ReleaseCandidate,
                    destinations,
                },
            }
        :   {};

    const upstreamBetaStepConfigOptions =
        upstreamBetaStepConfigId ?
            {
                [upstreamBetaStepConfigId]: {
                    id: upstreamBetaStepConfigId,
                    name: 'Beta Testing',
                    description: null,
                    archetype: ReleaseStepType.BetaTesting,
                    destinations,
                },
            }
        :   {};

    switch (archetype) {
        case ReleaseStepType.BetaTesting: {
            const config: BetaTestingStepConfig = {
                ...base,
                archetype: ReleaseStepType.BetaTesting,
                settings: {
                    betaTestingStyle: BetaTestingStyle.OpenEndedSoak,
                    prescribedBetaSoakDurationSeconds: 86400,
                    googleTestingTrackName: null,
                    upstreamCIStepConfigId: upstreamCIStepConfigId ?? null,
                    upstreamCIStepConfigOptions,
                },
            };
            return config;
        }

        case ReleaseStepType.Metadata: {
            const config: MetadataStepConfig = {
                ...base,
                archetype: ReleaseStepType.Metadata,
                settings: {
                    upstreamBetaStepConfigId: upstreamBetaStepConfigId ?? '',
                    upstreamBetaStepConfigOptions,
                    defaultMetadata: [],
                },
            };
            return config;
        }

        case ReleaseStepType.Submission: {
            const config: SubmissionStepConfig = {
                ...base,
                archetype: ReleaseStepType.Submission,
                settings: {
                    appStoreSettings: {},
                    encryptionSelection: null,
                    isAppAvailableInFrance: null,
                    upstreamCIStepConfigId: upstreamCIStepConfigId ?? null,
                    upstreamCIStepConfigOptions,
                },
            };
            return config;
        }

        case ReleaseStepType.StoreReview: {
            const config: StoreReviewStepConfig = {
                ...base,
                archetype: ReleaseStepType.StoreReview,
                settings: {
                    appStoreSettings: {},
                    encryptionSelection: null,
                    isAppAvailableInFrance: null,
                    upstreamCIStepConfigId: upstreamCIStepConfigId ?? null,
                    upstreamCIStepConfigOptions,
                },
            };
            return config;
        }

        case ReleaseStepType.Takeoff: {
            const config: ReleaseStepConfig = {
                ...base,
                archetype: ReleaseStepType.Takeoff,
                settings: {
                    appStoreSettings: {},
                    encryptionSelection: InitialEncryptionOptions.NoAlgorithms,
                    isAppAvailableInFrance: true,
                    upstreamCIStepConfigId: upstreamCIStepConfigId ?? null,
                    upstreamCIStepConfigOptions,
                },
            };
            return config;
        }

        default: {
            const config: DefaultStepConfig = {
                ...base,
                archetype,
                settings: null,
            };
            return config;
        }
    }
};
